'use memo'

import {
  LinkedDateImpact,
  RenderedClauseText,
  StaffPreviewLinkedDatesRequestSourceDateType,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
  useStaffUpdateContractSpecialConditionDueDate,
} from '@urbanx/agx.campaigns.client'
import dayjs from 'dayjs'
import {
  AgxButton,
  AgxDatePicker,
  AgxDivider,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import { useEffect, useState } from 'react'
import { toDateOnlyString } from 'utils/date'
import { LinkedDateWarningModal } from '../../modals/LinkedDateWarningModal'

interface SpecialConditionClauseProps {
  clause: RenderedClauseText
  conditionId: string
  contractId: string
  campaignId: string
  isLastClause: boolean
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDate: string | undefined
  ) => void
}

const SpecialConditionClause = ({
  clause,
  conditionId,
  contractId,
  campaignId,
  isLastClause,
  onDueDateUpdated,
}: SpecialConditionClauseProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [saveState, setSaveState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const [linkedDateImpacts, setLinkedDateImpacts] = useState<
    LinkedDateImpact[]
  >([])

  const saveButtonText =
    saveState === SAVE_BUTTON_STATES.SAVED
      ? 'Changes Saved'
      : saveState === SAVE_BUTTON_STATES.UNSAVED
        ? 'Save'
        : saveState === SAVE_BUTTON_STATES.ERROR
          ? 'Error'
          : saveState === SAVE_BUTTON_STATES.SAVING
            ? 'Saving...'
            : 'Save Changes'

  useEffect(() => {
    if (!isEditing) setSaveState(SAVE_BUTTON_STATES.UNSAVED)
  }, [isEditing])

  const [localDueDate, setLocalDueDate] = useState<Date | undefined>(() =>
    clause.dueDate ? dayjs(clause.dueDate).toDate() : undefined
  )
  const invalidateUseStaffGetContractDates =
    useInvalidateUseStaffGetContractDates()

  const { staffUpdateContractSpecialConditionDueDate } =
    useStaffUpdateContractSpecialConditionDueDate({
      onSuccess: async () => {
        setSaveState(SAVE_BUTTON_STATES.SAVED)
        setIsEditing(false)
        onDueDateUpdated(
          conditionId,
          clause.clauseId ?? '',
          toDateOnlyString(localDueDate)
        )
        await invalidateUseStaffGetContractDates(campaignId, contractId)
      },
      onError: () => {
        setSaveState(SAVE_BUTTON_STATES.ERROR)
        setIsEditing(false)
      },
    })

  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {
      setSaveState(SAVE_BUTTON_STATES.ERROR)
    },
  })

  const saveDueDate = () => {
    staffUpdateContractSpecialConditionDueDate({
      campaignId,
      contractId,
      conditionId,
      clauseId: clause.clauseId ?? '',
      dueDate: toDateOnlyString(localDueDate),
    })
  }

  const onSaveClick = async () => {
    if (!clause?.clauseId) return
    if (saveState !== SAVE_BUTTON_STATES.UNSAVED) return

    setSaveState(SAVE_BUTTON_STATES.SAVING)

    try {
      const preview = await staffPreviewLinkedDates({
        campaignId,
        contractId,
        body: {
          sourceDateType:
            StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          proposedDate: toDateOnlyString(localDueDate),
          specialConditionClauseId: clause.clauseId,
        },
      })

      const impactedDates = preview?.impactedDates ?? []
      if (impactedDates.length > 0) {
        setLinkedDateImpacts(impactedDates)
        setSaveState(SAVE_BUTTON_STATES.UNSAVED)
        return
      }

      saveDueDate()
    } catch {
      setSaveState(SAVE_BUTTON_STATES.ERROR)
    }
  }

  const onCancelLinkedDateWarning = () => {
    setLinkedDateImpacts([])
    setSaveState(SAVE_BUTTON_STATES.UNSAVED)
  }

  const onConfirmLinkedDateWarning = () => {
    setLinkedDateImpacts([])
    setSaveState(SAVE_BUTTON_STATES.SAVING)
    saveDueDate()
  }

  const onDueDateChange = (newDate: Date | null) => {
    setLocalDueDate(newDate ?? undefined)
  }

  return (
    <>
      <AgxSpacer fill direction="vertical" gap="md">
        <AgxTypography
          type="body_ui"
          size="small"
          id={`clause-${clause.clauseId}`}
          color="var(--text-secondary)"
        >
          Clause {clause.clausePosition}: {clause.clauseText}
        </AgxTypography>
        <AgxSpacer
          direction="horizontal"
          fill
          contentAlignment="end"
          gap="spacing-xl"
        >
          <AgxDatePicker
            id={`clause-${clause.clauseId}-date`}
            placeholder="No Due Date"
            disabled={!isEditing}
            required
            label="Due Date"
            stretch
            optionalText=""
            value={localDueDate}
            onChange={onDueDateChange}
          />

          {isEditing ? (
            <AgxSaveChangesButton
              state={saveState}
              id={`clause-${clause.clauseId}-save-button`}
              text={saveButtonText}
              variant={'primary'}
              onClick={onSaveClick}
            />
          ) : (
            <AgxButton
              id={`clause-${clause.clauseId}-button`}
              text={'Edit'}
              variant={'hollow'}
              size="medium"
              onClick={() => setIsEditing(true)}
            />
          )}
        </AgxSpacer>
      </AgxSpacer>
      {!isLastClause && <AgxDivider variant={'horizontal'} />}
      {linkedDateImpacts.length > 0 && (
        <LinkedDateWarningModal
          impactedDates={linkedDateImpacts}
          sourceDateTypes={[
            StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          ]}
          onCancel={onCancelLinkedDateWarning}
          onConfirm={onConfirmLinkedDateWarning}
        />
      )}
    </>
  )
}

export default SpecialConditionClause
